import React, { useState, useCallback, useEffect } from "react";
import { Box, IconButton, Chip, MenuItem, Button, Menu } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeviceSearchInput from "./DeviceSearchInput";
import { PrimaryTableChip } from "../Features/Table/GenericDraggableTable/TableStyles";
import { SearchIcon } from "../Features/Table/Icons";

const SearchContainer = styled(Box)(({ width }) => ({
    display: "flex",
    alignItems: "center",
    border: "1px solid #E6E6E6",
    borderRadius: "8px",
    width: width ? width : "60%",
    height: '32px',
    maxWidth: '100%',
    paddingRight: '12px',
    backgroundColor: '#FFFFFF'
}));

const ColumnSelectorButton = styled(Button)({
    backgroundColor: '#FAFAFA',
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
    borderTopRightRadius: "0px",
    borderBottomRightRadius: "0px",
    borderRight: '1px solid #E6E6E6',
    fontSize: '12px',
    color: '#3D4B5A',
    fontWeight: 400,
    lineHeight: '16px',
    textTransform: 'none',
    minWidth: 'auto',
    paddingLeft: '12px',
    paddingRight: '12px',
    "&:hover": {
        color: '#6D72F6',
        "& .MuiButton-endIcon .MuiSvgIcon-root": {
            color: '#6D72F6',
        }
    },
    "& .MuiButton-endIcon": {
        "& .MuiSvgIcon-root": {
            color: '#A2A2A2',
            fontSize: '18px !important',
        }
    }
});

const DropdownMenu = styled(Menu)({
    '& .MuiPaper-root': {
        width: 'auto',
        minWidth: '120px',
        padding: '12px',
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 4px 4px 0px #C0C0C052',
    }
});

const ColumnMenuItem = styled(MenuItem)({
    padding: '8px 12px',
    marginBottom: "4px",
    fontWeight: 400,
    fontSize: "12px",
    color: "#3D4B5A",
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    lineHeight: "24px",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
        fontWeight: 500,
        color: "#6D72F6",
        backgroundColor: "#F4F5FF",
    },
});

const ChipsWrapper = styled(Box)({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px",
    flexShrink: 0,
    paddingLeft: '4px'
});

const RemainingCountChip = styled(Chip)({
    background: "#F4F5FF",
    border: "1px solid #F4F5FF",
    borderRadius: "18px",
    padding: "0 4px",
    maxHeight: '22px',
    fontSize: "12px",
    color: "#6D72F6",
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

const ClearButton = styled(IconButton)({
    "&:hover": {
        backgroundColor: "#E6E6E6",
        color: "#970625",
    },
});

const VerticalSeparator = styled(Box)({
    marginLeft: '6px',
    marginRight: '8px',
    height: '18px',
    borderRight: '1px solid #CBCBCB',
    width: '1px'
});

const SearchExecuteButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'isEnabled'
})(({ isEnabled }) => ({
    pointerEvents: isEnabled ? "auto" : "none",
    cursor: isEnabled ? "pointer" : "default",
    padding: '0px'

}));

const SearchIconStyled = styled(SearchIcon)({
    fontSize: "16px",
    color: "#5E758D",
    padding: '0px'
});

const CloseIconStyled = styled(CloseIcon)({
    fontSize: "14px"
});

const SearchInputWrapper = styled(Box)({
    flexGrow: 1
});

const DEFAULT_COLUMNS = [{ key: 'deviceID', title: 'device_id' }];

const MAX_VISIBLE_CHIPS = 3;

const DigitalTwinTableSearch = ({
    onSearch = () => { },
    searchableFields = DEFAULT_COLUMNS,
    defaultColumn = null,
    container,
    containerWidth = null,
    maxSelections = null, // null = unlimited; 1 = single-select mode
    searchSuggestionsHandler = null,
}) => {
    const { t } = useTranslation(['common', 'generic_table']);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [selectedColumnKey, setSelectedColumnKey] = useState(defaultColumn || (searchableFields && searchableFields.length > 0 ? searchableFields[0]?.key : null));
    const [dropdownAnchorElement, setDropdownAnchorElement] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    const visibleDeviceChips = selectedDevices.slice(-MAX_VISIBLE_CHIPS);
    const hiddenDevicesCount = selectedDevices.length - visibleDeviceChips.length;
    const hasSelectedDevices = selectedDevices.length > 0;

    const getColumnTitle = (columnKey) => {
        const column = searchableFields.find(col => col.key === columnKey);
        return t(`common:${column?.title}`, column?.title);
    };

    const clearAllSelections = useCallback(() => {
        if (hasSelectedDevices) {
            setSelectedDevices([]);
            setHasSearched(false);
            if (hasSearched) {
                onSearch([], selectedColumnKey);
            }
        }
    }, [hasSelectedDevices, onSearch, selectedColumnKey, hasSearched]);

    const addDeviceToSelection = useCallback((newDevice) => {
        setSelectedDevices((currentDevices) => {
            if (currentDevices.includes(newDevice)) {
                return currentDevices;
            }
            if (maxSelections !== null && currentDevices.length >= maxSelections) {
                return [newDevice]; // replace existing selection
            }
            return [...currentDevices, newDevice];
        });
    }, [maxSelections]);

    const executeSearch = useCallback(() => {
        if (!hasSelectedDevices) {
            return false;
        }
        setHasSearched(true);
        onSearch(selectedDevices, selectedColumnKey);
    }, [hasSelectedDevices, onSearch, selectedDevices, selectedColumnKey]);

    const removeDeviceFromSelection = useCallback((deviceToRemove) => () => {
        if (selectedDevices.length === 1) {
            clearAllSelections();
            return;
        }

        setSelectedDevices((currentDevices) => {
            const updatedDevices = currentDevices.filter((device) => device !== deviceToRemove);
            if (hasSearched) {
                onSearch(updatedDevices, selectedColumnKey);
            }
            return updatedDevices;
        });
    }, [selectedDevices.length, clearAllSelections, onSearch, selectedColumnKey, hasSearched]);

    const openColumnDropdown = useCallback((event) => {
        setDropdownAnchorElement(event.currentTarget);
    }, []);

    const closeColumnDropdown = useCallback(() => {
        setDropdownAnchorElement(null);
    }, []);

    const selectColumn = useCallback((columnKey) => {
        setSelectedColumnKey(columnKey);
        closeColumnDropdown();
    }, [closeColumnDropdown]);

    const renderColumnDropdown = () => {
        if (searchableFields.length === 0) return null;

        return (
            <>
                <ColumnSelectorButton
                    onClick={openColumnDropdown}
                    endIcon={<KeyboardArrowDownIcon />}
                    data-testid="search-column-selector"
                >
                    {getColumnTitle(selectedColumnKey)}
                </ColumnSelectorButton>

                <DropdownMenu
                    anchorEl={dropdownAnchorElement}
                    open={Boolean(dropdownAnchorElement)}
                    onClose={closeColumnDropdown}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    container={container || undefined}
                >
                    {searchableFields.map((column) => (
                        <ColumnMenuItem
                            key={column.key}
                            onClick={() => selectColumn(column.key)}
                            data-testid={`search-column-option-${column.key}`}
                        >
                            {getColumnTitle(column.key)}
                        </ColumnMenuItem>
                    ))}
                </DropdownMenu>
            </>
        );
    };

    const renderDeviceChips = () => (
        <ChipsWrapper>
            {visibleDeviceChips.map((device, index) => (
                <PrimaryTableChip
                    key={`${device}-${index}`}
                    label={device}
                    onDelete={removeDeviceFromSelection(device)}
                    deleteIcon={<CloseRoundedIcon />}
                />
            ))}

            {hiddenDevicesCount > 0 && (
                <RemainingCountChip
                    key="remaining-count"
                    label={`+${hiddenDevicesCount}`}
                />
            )}
        </ChipsWrapper>
    );

    const renderClearAllButton = () => {
        if (!hasSelectedDevices) return null;

        return (
            <ClearButton
                onClick={clearAllSelections}
                size="small"
            >
                <CloseIconStyled />
            </ClearButton>
        );
    };

    const renderSeparator = () => {
        if (!hasSelectedDevices) return null;

        return <VerticalSeparator />;
    };

    const renderSearchExecuteButton = () => (
        <SearchExecuteButton
            onClick={executeSearch}
            size="small"
            isEnabled={hasSelectedDevices}
            disableRipple
            disableTouchRipple
            disableFocusRipple
        >
            <SearchIconStyled />
        </SearchExecuteButton>
    );

    useEffect(() => {
        if (maxSelections !== null && selectedDevices.length > 0) {
            setHasSearched(true);
            onSearch(selectedDevices, selectedColumnKey);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDevices]);

    useEffect(() => {
        clearAllSelections()
        setHasSearched(false);
    }, [selectedColumnKey]);

    return (
        <SearchContainer width={containerWidth}>
            {renderColumnDropdown()}
            {renderDeviceChips()}

            {(maxSelections === null || selectedDevices.length < maxSelections) ? (
                <SearchInputWrapper>
                    <DeviceSearchInput
                        placeholder={t('generic_table:search_devices', {
                            device_id: selectedColumnKey
                                ? getColumnTitle(selectedColumnKey)
                                : t('common:device_id')
                        })}
                        columnName={selectedColumnKey}
                        onSelect={addDeviceToSelection}
                        onExecuteSearch={executeSearch}
                        disableSuggestions={false}
                        container={container}
                        {...(searchSuggestionsHandler ? { searchSuggestionsHandler } : {})}
                    />
                </SearchInputWrapper>
            ) : (
                <Box sx={{ flexGrow: 1 }} />
            )}

            {renderClearAllButton()}
            {renderSeparator()}
            {renderSearchExecuteButton()}
        </SearchContainer>
    );
}

export default DigitalTwinTableSearch;
